import { reportDeviceNotApproved } from '../state/device'
import type { DeviceStatus, ProblemDetails } from './types'

/** Error returned by the API as application/problem+json (or any non-2xx response). */
export class ApiError extends Error {
  readonly status: number
  readonly title: string
  readonly detail: string | undefined
  /** Seconds from a Retry-After header (429). */
  readonly retryAfter: number | undefined
  /** Machine-readable `error` from the problem JSON, e.g. "device_not_approved". */
  readonly error: string | undefined
  /** The parsed problem body, when there was one. */
  readonly problem: Partial<ProblemDetails> | undefined

  constructor(status: number, title: string, detail?: string, retryAfter?: number, problem?: Partial<ProblemDetails>) {
    super(detail ? `${title}: ${detail}` : title)
    this.name = 'ApiError'
    this.status = status
    this.title = title
    this.detail = detail
    this.retryAfter = retryAfter
    this.problem = problem
    this.error = typeof problem?.error === 'string' ? problem.error : undefined
  }
}

export const DEVICE_NOT_APPROVED = 'device_not_approved'

export function isDeviceNotApproved(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 403 && error.error === DEVICE_NOT_APPROVED
}

/** 409 for a membership row an integration controls (leave, remove, role change). */
export const MANAGED_MEMBERSHIP = 'managed_membership'
/** 409 for a managed conversation's name, topic, archive flag, or manual additions under a managed_only policy. */
export const MANAGED_CHANNEL = 'managed_channel'
export const MANAGED_CHANGE_MESSAGE = "This is managed automatically, so it can't be changed here"

/** 409 from reply-conversation: the channel is not restricted, is archived, or its reply bot is off or unavailable. */
export const REPLIES_OFF = 'replies_off'
export const REPLIES_PAUSED_MESSAGE = 'Private replies are paused for this channel'
/** 400 when a private reply names a message it can't answer (deleted, replies turned off, a different bot). */
export const INVALID_CONTEXT = 'invalid_context'
export const INVALID_CONTEXT_MESSAGE = "This private reply can't be sent: the message it answers can't take replies any more"
/** 400 when the chosen reply bot can't receive messages (no webhook, revoked). */
export const INVALID_REPLY_BOT = 'invalid_reply_bot'
export const INVALID_REPLY_BOT_MESSAGE = "That bot can't receive replies. Choose a bot with a webhook."

/** 409 when forwarding a message that has been deleted. */
export const MESSAGE_DELETED = 'message_deleted'
export const MESSAGE_DELETED_MESSAGE = 'This message was deleted'
/** 409, with Retry-After, while a video or audio clip in the message is still being converted. */
export const MEDIA_PROCESSING = 'media_processing'
export const MEDIA_PROCESSING_MESSAGE = 'A clip in this message is still processing. Try again in a moment.'
/** 409 when a clip in the message could not be converted, which never changes. */
export const MEDIA_FAILED = 'media_failed'
/** 409 when editing a forwarded message: the words are the original author's. */
export const FORWARDED_MESSAGE = 'forwarded_message'
export const FORWARDED_MESSAGE_MESSAGE = "Forwarded messages can't be edited"

export function isManagedConflict(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409 && (error.error === MANAGED_MEMBERSHIP || error.error === MANAGED_CHANNEL)
}

export function isRepliesOff(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409 && error.error === REPLIES_OFF
}

/** The request never reached the server (offline, DNS, connection reset, CORS). */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Network request failed')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

/**
 * What to do on 401:
 * - "redirect": the session expired while using the app → go to the IdP (when online).
 * - "gate": let the caller show the sign-in screen (used for GET /api/me).
 */
export type UnauthorizedMode = 'redirect' | 'gate'

type UnauthorizedHandler = (mode: UnauthorizedMode) => void

let unauthorizedHandler: UnauthorizedHandler = () => {}

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler
}

export const CSRF_HEADER = 'X-Requested-With'
export const CSRF_VALUE = 'boss-chat'

export type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Sent as JSON. */
  body?: unknown
  /** Sent as is (e.g. an image), with the blob's own type as Content-Type. Ignored when `body` is set. */
  rawBody?: Blob
  query?: Record<string, QueryValue>
  signal?: AbortSignal
  onUnauthorized?: UnauthorizedMode
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path
}

async function parseError(response: Response): Promise<ApiError> {
  const retryHeader = response.headers.get('Retry-After')
  const retryAfter = retryHeader && /^\d+$/.test(retryHeader) ? Number(retryHeader) : undefined
  let title = response.statusText || `HTTP ${response.status}`
  let detail: string | undefined
  let problem: Partial<ProblemDetails> | undefined
  const type = response.headers.get('Content-Type') ?? ''
  if (type.includes('json')) {
    try {
      const body: unknown = await response.json()
      if (body && typeof body === 'object') {
        problem = body as Partial<ProblemDetails>
        if (typeof problem.title === 'string') title = problem.title
        if (typeof problem.detail === 'string') detail = problem.detail
      }
    } catch {
      // ignore malformed bodies
    }
  }
  return new ApiError(response.status, title, detail, retryAfter, problem)
}

/** Same-origin fetch wrapper for /api and /auth. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (method !== 'GET') headers[CSRF_HEADER] = CSRF_VALUE
  let body: BodyInit | undefined
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  } else if (options.rawBody !== undefined) {
    headers['Content-Type'] = options.rawBody.type || 'application/octet-stream'
    body = options.rawBody
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new NetworkError(error)
  }

  if (response.status === 401) {
    unauthorizedHandler(options.onUnauthorized ?? 'redirect')
    throw await parseError(response)
  }
  if (!response.ok) {
    const error = await parseError(response)
    // Enforce mode: this session's device is not approved. Go to the gate instead of surfacing an error.
    if (isDeviceNotApproved(error)) {
      reportDeviceNotApproved(error.problem?.deviceStatus as DeviceStatus | undefined, error.problem?.code)
    }
    throw error
  }
  if (response.status === 204 || response.status === 205) return undefined as T
  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError
}

/** 4xx other than 408/429 means retrying the same request will not help. */
export function isPermanentFailure(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429 &&
    error.status !== 401 &&
    // A device waiting for approval should keep its queued messages for when it is approved.
    !isDeviceNotApproved(error)
  )
}

export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof NetworkError) return "You're offline or the server can't be reached"
  if (isDeviceNotApproved(error)) return 'This device is not approved yet'
  if (isManagedConflict(error)) return MANAGED_CHANGE_MESSAGE
  if (isRepliesOff(error)) return REPLIES_PAUSED_MESSAGE
  if (error instanceof ApiError && error.error === INVALID_CONTEXT) return INVALID_CONTEXT_MESSAGE
  if (error instanceof ApiError && error.error === INVALID_REPLY_BOT) return INVALID_REPLY_BOT_MESSAGE
  if (error instanceof ApiError && error.status === 409 && error.error === MESSAGE_DELETED) return MESSAGE_DELETED_MESSAGE
  if (error instanceof ApiError && error.status === 409 && error.error === MEDIA_PROCESSING) return MEDIA_PROCESSING_MESSAGE
  if (error instanceof ApiError && error.status === 409 && error.error === FORWARDED_MESSAGE) return FORWARDED_MESSAGE_MESSAGE
  if (error instanceof ApiError) return error.detail ?? error.title
  if (error instanceof Error && error.message) return error.message
  return fallback
}

/**
 * Toast text for a failed user action: "{prefix} {reason}". A managed conflict stands on its own, because the
 * prefix ("Couldn't leave.") adds nothing to "This is managed automatically, so it can't be changed here".
 */
export function actionErrorMessage(prefix: string, error: unknown): string {
  if (isManagedConflict(error)) return MANAGED_CHANGE_MESSAGE
  return prefix ? `${prefix} ${errorMessage(error)}` : errorMessage(error)
}
